/**
 * Shared autosave-status hook + indicator component used by detail
 * panels (WBS and migration-task) to surface a tiny "保存中… / 保存済み ✓
 *  / 保存に失敗しました" badge in the panel header.
 *
 * Originally lived inline in {@link ./../components/Wbs/WbsItemDetailPanel.tsx};
 * extracted here so the migration-task `TaskDetailPanel` can reuse the
 * exact same primitive (and a future regression to either surface is
 * one bug fix, not two).
 *
 * Contract:
 *   - `run(op)` flips status to `saving`, awaits the op, then to
 *     `saved`; out-of-order completions don't downgrade a later in-flight
 *     save (each op gets a monotonically-increasing id and only the
 *     latest started op gets to show "saved").
 *   - On error the status sticks at `error` until the next save attempt.
 *   - The "saved" badge auto-fades back to `idle` after ~1.5s.
 *   - The hook clears its fade timer on unmount.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** How long the "保存済み ✓" badge sticks before fading back to idle. */
const SAVED_BADGE_MS = 1500;

export interface AutosaveStatusApi {
  status: SaveStatus;
  run: (op: () => Promise<void>) => Promise<void>;
}

export function useAutosaveStatus(): AutosaveStatusApi {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tag each save attempt so an out-of-order completion (slow first +
  // fast second) doesn't let "saved" overwrite a later "saving".
  const opCounter = useRef(0);
  const lastStartedRef = useRef(0);
  const lastSettledRef = useRef(0);

  useEffect(() => {
    return () => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, []);

  const run = useCallback(async (op: () => Promise<void>) => {
    const id = ++opCounter.current;
    lastStartedRef.current = id;
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current);
      fadeTimer.current = null;
    }
    setStatus('saving');
    try {
      await op();
      if (id < lastSettledRef.current) return;
      lastSettledRef.current = id;
      // Don't downgrade an in-flight "saving" if a later op is still
      // pending — only the latest started op gets to show "saved".
      if (id !== lastStartedRef.current) return;
      setStatus('saved');
      fadeTimer.current = setTimeout(() => {
        setStatus((s) => (s === 'saved' ? 'idle' : s));
        fadeTimer.current = null;
      }, SAVED_BADGE_MS);
    } catch (err) {
      lastSettledRef.current = id;
      // eslint-disable-next-line no-console
      console.error('[autosave] failed', err);
      setStatus('error');
      throw err;
    }
  }, []);

  return { status, run };
}

/**
 * Tiny header indicator. Renders nothing when idle so the panel chrome
 * stays quiet while the user isn't editing. Both the WBS and migration-
 * task detail panels mount this next to the close button.
 */
export function AutosaveIndicator({
  status,
  testId = 'autosave-status',
}: {
  status: SaveStatus;
  /** Optional `data-testid` override; defaults to the shared one. */
  testId?: string;
}): JSX.Element | null {
  if (status === 'idle') return null;
  if (status === 'saving') {
    return (
      <span
        className="text-[11px] text-slate-500"
        data-testid={testId}
        data-status="saving"
      >
        保存中…
      </span>
    );
  }
  if (status === 'saved') {
    return (
      <span
        className="text-[11px] text-emerald-600"
        data-testid={testId}
        data-status="saved"
      >
        保存済み ✓
      </span>
    );
  }
  return (
    <span
      className="text-[11px] text-red-600"
      data-testid={testId}
      data-status="error"
    >
      保存に失敗しました
    </span>
  );
}
